import { Loader2 } from 'lucide-react'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { SignerChoice } from '@/lib/signerChoices'

interface SignerPickerProps {
  /** "Sign as" / "Remove as" — the legacy menu's own words. */
  label: string
  choices: SignerChoice[]
  value: string | null
  onChange: (id: string) => void
  loading?: boolean
  error?: string | null
  disabled?: boolean
}

/**
 * The "Sign as:" / "Remove as:" dropdown (z8nrz7dt3z). Every active person is
 * listed alphabetically; people who may not give this signature are shown with
 * the reason and cannot be picked, so the restriction is visible BEFORE a
 * password is typed (AC2). Deactivated accounts never appear (AC13) — the
 * server leaves them out of the roster.
 */
export function SignerPicker({
  label,
  choices,
  value,
  onChange,
  loading = false,
  error = null,
  disabled = false,
}: SignerPickerProps) {
  const eligibleCount = choices.filter(c => c.eligible).length
  return (
    <div className='space-y-1.5'>
      <Label htmlFor='signer-picker' className='text-xs'>
        {label}
      </Label>
      <Select value={value ?? undefined} onValueChange={onChange} disabled={disabled || loading}>
        <SelectTrigger id='signer-picker' className='h-9 text-sm'>
          {loading ? (
            <span className='flex items-center gap-2 text-muted-foreground'>
              <Loader2 className='h-3.5 w-3.5 animate-spin' /> Loading people…
            </span>
          ) : (
            <SelectValue placeholder='Choose who is signing…' />
          )}
        </SelectTrigger>
        <SelectContent className='max-h-72'>
          {choices.map(c => (
            <SelectItem key={c.id} value={c.id} disabled={!c.eligible}>
              <span className='flex flex-col'>
                <span>
                  {c.name}
                  {c.isSelf && <span className='text-muted-foreground'> (you)</span>}
                </span>
                {!c.eligible && c.reason && (
                  <span className='text-[11px] text-muted-foreground'>{c.reason}</span>
                )}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* Everyone is listed (AC2), so say plainly how many can actually sign
          — otherwise a restricted list reads as unrestricted at a glance. */}
      {!loading && choices.length > 0 && (
        <p className='text-[11px] text-muted-foreground'>
          {eligibleCount === choices.length
            ? 'Anyone listed can sign.'
            : eligibleCount === 0
              ? 'Nobody listed can sign this — see each person for why.'
              : `${eligibleCount} of ${choices.length} people can sign; the rest are shown greyed out with the reason.`}
        </p>
      )}
      {error && <p className='text-xs text-destructive'>{error}</p>}
    </div>
  )
}
